import { resourceReservationIdZodSchema } from "@@/shared/acquire/resources/resource-reservation-id";
import { dateRangeZodObject } from "@@/shared/date-time/date-range";
import { timeRangeZodSchema } from "@@/shared/date-time/time-range";
import { optionalIdZodSchema } from "@@/shared/optionals/optional-id";
import { orderItemIdZodSchema } from "@@/shared/orders/order-items/order-item-id";
import { productIdZodSchema } from "@@/shared/products/product-id";
import { providerIdZodSchema } from "@@/shared/providers/provider-id";
import {
    requiredTranslatableZodSchema,
    translatableZodSchema,
} from "@@/shared/translations/translation";
import { z } from "zod";
import { contactInfoZodObject } from "../../locations/contact-info";
import { orderGroupIdZodSchema } from "../../order-groups/order-group-id";
import { orderIdZodSchema } from "../../orders/order-id";
import { agendaItem_ReservationDataZodObject } from "./agenda-reservation-data";

const agendaItem_Reservation_ExtraZodObject = z.object({
    orderItemId: orderItemIdZodSchema,
    label: translatableZodSchema,
    quantity: z.number().int().min(1),
    parentId: orderItemIdZodSchema.optional(),
    optionalId: optionalIdZodSchema.optional(),
});
type AgendaItem_Reservation_Extra = z.infer<
    typeof agendaItem_Reservation_ExtraZodObject
>;

const agendaItem_ReservationZodObject = z.object({
    _id: resourceReservationIdZodSchema,
    _type: z.literal("AGENDA_ITEM"),
    type: z.literal("RESERVATION"),
    customerInfo: contactInfoZodObject,
    providerId: providerIdZodSchema,
    orderId: orderIdZodSchema,
    orderGroupId: orderGroupIdZodSchema,
    orderGroupReference: z.string().min(1),
    orderSlug: z.string(),
    orderProductIds: productIdZodSchema.array(),
    orderMainProductTitle: requiredTranslatableZodSchema,
    orderMainProductId: productIdZodSchema.optional(),
    orderMessageFromCustomer: z.string().optional(),
    orderDateRange: dateRangeZodObject,
    orderTimeRange: timeRangeZodSchema,
    isOnPremiseOrder: z.boolean().default(false),
    hasComments: z.boolean().default(false),
    hasWarnings: z.boolean().default(false),
    extras: z.array(agendaItem_Reservation_ExtraZodObject),
    unpaidAmount: z.number().int().min(0), // priceZodSchema.optional(),
    /** Quantities to be used for calculating and showing stats in the agenda */
    statQuantities: z.array(
        z.object({
            label: requiredTranslatableZodSchema,
            quantity: z.number(),
        }),
    ),
    // Reservations for the order
    reservation: agendaItem_ReservationDataZodObject,
    otherOrderReservations: z
        .array(agendaItem_ReservationDataZodObject)
        .default([]),
});

type AgendaItem_Reservation = z.infer<typeof agendaItem_ReservationZodObject>;

const isAgendaItem_Reservation = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    item: any,
): item is AgendaItem_Reservation => {
    return (
        (item as AgendaItem_Reservation)._type === "AGENDA_ITEM" &&
        item.type === "RESERVATION"
    );
};

export {
    agendaItem_Reservation_ExtraZodObject,
    agendaItem_ReservationZodObject,
    isAgendaItem_Reservation,
};
export type { AgendaItem_Reservation, AgendaItem_Reservation_Extra };
